import { IMPORT_TYPES } from '../../app/terminology/imports';
import { FEATURES } from '../../app/terminology/features';
import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    DataTable,
    DropZone,
    Icon,
    InlineStack,
    Layout,
    Modal,
    Page,
    ProgressBar,
    Select,
    Text,
    TextField,
    Thumbnail,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ClockIcon, CodeIcon, CollectionIcon, ConnectIcon, FileIcon, NoteIcon, ProductIcon, RefreshIcon, SettingsIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { AcesImportSetup, AtdPriceAdjustmentMode, CollectionMappingImportPreview, ConfiguratorAssignmentMode, CsvXlsxImportMode, CsvXlsxImportPreview, FitmentImportHistoryItem, ImportFormat, ProductTagFormatPreset, ProductTagImportPreview, ProductTagValidationMode } from '../../app/api/agent';
import { AtdProfile } from '../../app/models/Atd';
import { ProductGuideCollectionOption, ProductGuideSummary } from '../../app/models/ProductGuide';
import { useStore } from '../../app/stores/store';
import { isProductGuideEnabledForCurrentContext } from '../../app/utils/productGuideFeature';
import { useLegacyMode } from '../legacy/LegacyModeContext';
import ImportHistoryCards from './ImportHistoryCards';

type UploadState = {
    file?: File;
    error?: string;
    success?: string;
};

type AcesSetupState = {
    queueId: number;
    details: AcesImportSetup;
    selectedVendor: string;
    skuSuffixRule: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
};

type VendorSkuRuleSettings = {
    skuPrefix: string;
    normalizeSku: boolean;
};

type ImportsPageView = 'imports' | 'exports';

const noVendorOptionValue = '__NO_VENDOR__';

type SelectableImportFormat = Exclude<ImportFormat, 'fitment_export'>;

type ImportFormatOption = {
    label: string;
    description: string;
    helperText: string;
    value: SelectableImportFormat;
};

const fitmentImportFormatOptions: ImportFormatOption[] = [
    {
        label: IMPORT_TYPES.csv_xlsx.label,
        description: 'Import product and variant fitments from CSV or Excel.',
        helperText: 'Import fitments from CSV or XLSX',
        value: 'csv_xlsx',
    },
    {
        label: IMPORT_TYPES.aces_xml.label,
        description: 'Import an ACES XML catalog and match vendor SKUs.',
        helperText: 'Import ACES XML and match vendor SKUs',
        value: 'aces_xml',
    },
    {
        label: IMPORT_TYPES.product_tags.label,
        description: 'Build fitments from tags on synced Shopify products.',
        helperText: 'Build fitments from synced Shopify tags',
        value: 'product_tags',
    },
];

const collectionMappingFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.collection_mappings_csv_xlsx.label,
    description: 'Assign vehicles and year ranges to Shopify collections.',
    helperText: 'Map vehicles or year ranges to collections',
    value: 'collection_mappings_csv_xlsx',
};

const configuratorDataFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.configurator_data_csv_xlsx.label,
    description: 'Bulk update variant attributes and apply a configurator to valid products.',
    helperText: 'Import variant metafields and product assignments',
    value: 'configurator_data_csv_xlsx',
};

const atdPriceFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.atd_price_file.label,
    description: 'Update Shopify variant prices from an ATD price file.',
    helperText: 'Update variant prices from an ATD file',
    value: 'atd_price_file',
};

const isPreviewedSpreadsheetFormat = (format: ImportFormat) =>
    format === 'csv_xlsx'
    || format === 'collection_mappings_csv_xlsx'
    || format === 'configurator_data_csv_xlsx';

const priceAdjustmentOptions = [
    { label: 'No adjustment', value: 'none' },
    { label: 'Increase by percentage', value: 'percent' },
    { label: 'Increase by fixed amount', value: 'fixed' },
];

const csvXlsxImportModeOptions = [
    {
        label: 'Add to existing fitments',
        description: 'Keep current assignments and add the fitments in this file.',
        value: 'append',
    },
    {
        label: 'Replace matched SKUs',
        description: 'Clear and replace fitments only for SKUs matched by this file.',
        value: 'replace_matched_skus',
    },
];

const configuratorAssignmentModeOptions = [
    {
        label: 'Update attributes and assign configurator',
        description: 'Write variant metafields, validate each product, and assign valid products to the selected configurator.',
        value: 'update_and_assign',
    },
    {
        label: 'Update attributes only',
        description: 'Write variant metafields without changing product configurator assignments.',
        value: 'update_only',
    },
];

const productTagFormatOptions = [
    { label: 'App default tag format', value: 'app_default' },
    { label: 'Turn14 fits_ tag', value: 'turn14' },
    { label: 'Make-Model-Year', value: 'make_model_year' },
    { label: 'Make-Model-Submodel-Year', value: 'make_model_submodel_year' },
    { label: 'Year-Make-Model', value: 'year_make_model' },
    { label: 'Custom pattern', value: 'custom' },
];

const productTagValidationOptions = [
    { label: 'Create missing vehicle values', value: 'create_missing' },
    { label: 'Only import existing vehicle values', value: 'existing_only' },
];

const importsPageViewOptions = [
    { value: 'imports' as ImportsPageView, label: 'Imports' },
    { value: 'exports' as ImportsPageView, label: 'Exports' },
];

const segmentedToggleStyle: React.CSSProperties = {
    display: 'inline-flex',
    gap: 2,
    padding: 3,
    border: '1px solid #d0d5dd',
    borderRadius: 14,
    background: '#f7f7f7',
};

const getSegmentedToggleButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    minHeight: 34,
    padding: '6px 18px',
    border: '1px solid transparent',
    borderRadius: 11,
    background: isSelected ? '#fff' : 'transparent',
    boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none',
    color: '#202223',
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: isSelected ? 600 : 400,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
});

type ImportsExportsToggleProps = {
    value: ImportsPageView;
    onChange: (value: ImportsPageView) => void;
};

const ImportsExportsToggle = ({ value, onChange }: ImportsExportsToggleProps) => (
    <div
        aria-label="Imports and exports view"
        role="group"
        style={segmentedToggleStyle}
    >
        {importsPageViewOptions.map((option) => {
            const isSelected = value === option.value;

            return (
                <button
                    key={option.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onChange(option.value)}
                    style={getSegmentedToggleButtonStyle(isSelected)}
                >
                    {option.label}
                </button>
            );
        })}
    </div>
);

const importFormatIcons = {
    csv_xlsx: FileIcon,
    aces_xml: CodeIcon,
    product_tags: ProductIcon,
    collection_mappings_csv_xlsx: CollectionIcon,
    configurator_data_csv_xlsx: SettingsIcon,
    atd_price_file: NoteIcon,
};

type ImportTypeCardProps = {
    description: string;
    label: string;
    format: SelectableImportFormat | 'turn14';
    lastUsed?: boolean;
    onChange: () => void;
};

const ImportTypeCard = ({ description, label, format, lastUsed, onChange }: ImportTypeCardProps) => (
    <button type="button" className="pf-import-type-card" onClick={onChange}>
        <span className="pf-import-type-card__icon"><Icon source={format === 'turn14' ? ConnectIcon : importFormatIcons[format]} tone="subdued" /></span>
        <span className="pf-import-type-card__content">
            <span className="pf-import-type-card__title">
                <Text as="span" variant="bodyMd" fontWeight="semibold">{label}</Text>
                {lastUsed && <Badge>Last used</Badge>}
            </span>
            <Text as="span" variant="bodySm" tone="subdued">{description}</Text>
        </span>
        <Icon source={ChevronRightIcon} tone="subdued" />
    </button>
);

const getRadioRowStyle = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    background: isSelected ? 'var(--p-color-bg-surface-selected)' : 'transparent',
    cursor: 'pointer',
});

type RadioRowProps = {
    checked: boolean;
    description: string;
    id: string;
    label: string;
    name: string;
    onChange: () => void;
};

const RadioRow = ({ checked, description, id, label, name, onChange }: RadioRowProps) => (
    <label htmlFor={id} style={getRadioRowStyle(checked)}>
        <input
            id={id}
            type="radio"
            name={name}
            checked={checked}
            onChange={onChange}
            style={{ margin: '3px 0 0', accentColor: '#005bd3', flexShrink: 0 }}
        />
        <span style={{ display: 'block', minWidth: 0 }}>
            <Text as="span" variant="bodyMd" fontWeight="medium">{label}</Text>
            <span style={{ display: 'block', marginTop: 2 }}>
                <Text as="span" variant="bodySm" tone="subdued">{description}</Text>
            </span>
        </span>
    </label>
);

const impactSummaryStyle: React.CSSProperties = {
    padding: 12,
    borderRadius: 8,
    background: 'var(--p-color-bg-surface-secondary)',
    border: '1px solid var(--p-color-border-secondary)',
};

const formatFileSize = (size: number) => {
    if (size < 1024) {
        return `${size} bytes`;
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const previewTableWrapperStyle: React.CSSProperties = {
    overflowX: 'auto',
};

const previewTableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
};

const previewTableCellStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--p-color-border-secondary)',
    textAlign: 'left',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
};

type CsvPreviewTableProps = {
    headings: string[];
    rows: string[][];
};

const CsvPreviewTable = ({ headings, rows }: CsvPreviewTableProps) => (
    <div style={previewTableWrapperStyle}>
        <table style={previewTableStyle}>
            <thead>
                <tr>
                    {headings.map((heading) => (
                        <th key={heading} scope="col" style={previewTableCellStyle}>
                            <Text as="span" variant="bodyMd" fontWeight="regular">{heading}</Text>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, rowIndex) => (
                    <tr key={`csv-preview-row-${rowIndex}`}>
                        {row.map((cell, cellIndex) => (
                            <td key={`csv-preview-cell-${rowIndex}-${cellIndex}`} style={previewTableCellStyle}>
                                {cell || '-'}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const hasConnectedAtdPriceProfile = (profile: AtdProfile) => {
    if (!profile?.isActive) {
        return false;
    }

    // The profile endpoint returns a default unsaved profile, so require the
    // standard API credential fields that indicate a merchant actually connected ATD.
    return !!(
        profile.standardBaseUrl?.trim() &&
        profile.clientId?.trim() &&
        profile.username?.trim() &&
        profile.password?.trim() &&
        profile.locationNumber?.trim()
    );
};

const ImportsPage = () => {
    const navigate = useNavigate();
    const { userStore } = useStore();
    const { isLegacy } = useLegacyMode();
    const canUseConfigurators = isProductGuideEnabledForCurrentContext(userStore.user?.shopDomain);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedPageView: ImportsPageView = searchParams.get('view') === 'exports' ? 'exports' : 'imports';
    const pageView: ImportsPageView = isLegacy ? 'imports' : requestedPageView;
    const [importFormat, setImportFormat] = useState<ImportFormat>('csv_xlsx');
    const [hasSelectedImportFormat, setHasSelectedImportFormat] = useState(false);
    const [showMoreImportOptions, setShowMoreImportOptions] = useState(false);
    const [showTurn14Sources, setShowTurn14Sources] = useState(false);
    const [lastSuccessfulFormat, setLastSuccessfulFormat] = useState<ImportFormat | null>(null);
    const [uploadState, setUploadState] = useState<UploadState>({});
    const [exportState, setExportState] = useState<Pick<UploadState, 'error' | 'success'>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingImport = useRef(false);
    const [importCapacity, setImportCapacity] = useState<{ activeCount: number; limit: number }>();
    const importQueueFull = !!importCapacity && importCapacity.activeCount >= importCapacity.limit;
    const importSubmissionDisabled = !importCapacity || importQueueFull;
    const importCapacityMessage = `You have ${importCapacity?.limit ?? 2} imports queued or running. Wait for one to finish before adding another.`;
    const [uploadProgressPercent, setUploadProgressPercent] = useState<number | undefined>();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const historyRequest = useRef(0);
    // An import/archive may finish after the user changes views. Refresh the current view.
    const reloadHistory = useRef<() => Promise<void>>(async () => {});
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [isConfirmingAcesSetup, setIsConfirmingAcesSetup] = useState(false);
    const [acesSetup, setAcesSetup] = useState<AcesSetupState | undefined>();
    const [vendorSuffixRulesByVendor, setVendorSuffixRulesByVendor] = useState<Record<string, VendorSkuRuleSettings>>({});
    const [canUseAtdPriceFile, setCanUseAtdPriceFile] = useState(false);
    const [priceAdjustmentMode, setPriceAdjustmentMode] = useState<AtdPriceAdjustmentMode>('none');
    const [priceAdjustmentValue, setPriceAdjustmentValue] = useState('');
    const [skipZeroMapPrices, setSkipZeroMapPrices] = useState(true);
    const [productTagFormatPreset, setProductTagFormatPreset] = useState<ProductTagFormatPreset>('app_default');
    const [productTagCustomFormat, setProductTagCustomFormat] = useState('{make}-{model}-{year}');
    const [productTagValidationMode, setProductTagValidationMode] = useState<ProductTagValidationMode>('create_missing');
    const [productTagPreview, setProductTagPreview] = useState<ProductTagImportPreview | undefined>();
    const [productTagPreviewError, setProductTagPreviewError] = useState<string | undefined>();
    const [reviewingProductTagIds, setReviewingProductTagIds] = useState<Record<number, boolean>>({});
    const [approvingProductTagIds, setApprovingProductTagIds] = useState<Record<number, boolean>>({});
    const [undoingProductTagIds, setUndoingProductTagIds] = useState<Record<number, boolean>>({});
    const [productTagUndoTarget, setProductTagUndoTarget] = useState<FitmentImportHistoryItem>();
    const [productTagUndoError, setProductTagUndoError] = useState<string>();
    const productTagUndoBusy = !!productTagUndoTarget && !!undoingProductTagIds[productTagUndoTarget.queueId];
    const [csvPreview, setCsvPreview] = useState<CsvXlsxImportPreview | CollectionMappingImportPreview | undefined>();
    const [csvPreviewFileKey, setCsvPreviewFileKey] = useState<string | undefined>();
    const [isCsvPreviewModalOpen, setIsCsvPreviewModalOpen] = useState(false);
    const [isPreviewingCsv, setIsPreviewingCsv] = useState(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const [isQueueingExport, setIsQueueingExport] = useState(false);
    const queueingExport = useRef(false);
    const [hasActiveExport, setHasActiveExport] = useState<boolean>();
    const [csvXlsxImportMode, setCsvXlsxImportMode] = useState<CsvXlsxImportMode>('append');
    const [configurators, setConfigurators] = useState<ProductGuideSummary[]>([]);
    const [configuratorCollections, setConfiguratorCollections] = useState<ProductGuideCollectionOption[]>([]);
    const [selectedConfiguratorId, setSelectedConfiguratorId] = useState('');
    const [configuratorTemplateCollectionId, setConfiguratorTemplateCollectionId] = useState('');
    const [configuratorAssignmentMode, setConfiguratorAssignmentMode] = useState<ConfiguratorAssignmentMode>('update_and_assign');

    useEffect(() => {
        if (!isLegacy) {
            return;
        }

        setImportFormat('csv_xlsx');
        setHasSelectedImportFormat(true);
    }, [isLegacy]);

    const importFormatOptions = useMemo(
        () => {
            const options = [...fitmentImportFormatOptions];
            if (canUseConfigurators) options.push(configuratorDataFormatOption);
            options.push(collectionMappingFormatOption);
            if (canUseAtdPriceFile) options.push(atdPriceFormatOption);
            return options;
        },
        [canUseAtdPriceFile, canUseConfigurators]
    );
    const selectedImportFormatOption = useMemo(
        () => importFormatOptions.find((option) => option.value === importFormat) ?? fitmentImportFormatOptions[0],
        [importFormat, importFormatOptions]
    );
    const primaryImportFormatOption = importFormatOptions.find((option) => option.value === lastSuccessfulFormat)
        ?? fitmentImportFormatOptions[0];

    const handlePageViewChange = useCallback((value: ImportsPageView) => {
        const updatedSearchParams = new URLSearchParams(searchParams);

        if (value === 'exports') {
            updatedSearchParams.set('view', 'exports');
        } else {
            updatedSearchParams.delete('view');
        }

        setSearchParams(updatedSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const isValidFileForFormat = useCallback((file: File, format: ImportFormat) => {
        const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';

        if (format === 'product_tags') {
            return true;
        }

        if (isPreviewedSpreadsheetFormat(format) || format === 'atd_price_file') {
            return extension === 'csv' || extension === 'xlsx';
        }

        return extension === 'xml';
    }, []);

    const validationHint = useMemo(() => {
        if (importFormat === 'product_tags') {
            return 'No file required. The importer reads product tags from the synced Shopify product cache.';
        }

        return isPreviewedSpreadsheetFormat(importFormat) || importFormat === 'atd_price_file'
            ? 'Accepted file types: .csv, .xlsx'
            : 'Accepted file types: .xml';
    }, [importFormat]);

    const getInvalidFileMessage = useCallback((format: ImportFormat) => {
        if (format === 'product_tags') {
            return 'Product Tags import does not require a file.';
        }

        if (format === 'aces_xml') {
            return 'Invalid file type. Select a .xml file.';
        }

        return 'Invalid file type. Select a .csv or .xlsx file.';
    }, []);

    const getFileKey = useCallback((file?: File) => {
        return file ? `${file.name}|${file.size}|${file.lastModified}` : undefined;
    }, []);

    const selectedFileKey = useMemo(() => getFileKey(uploadState.file), [getFileKey, uploadState.file]);
    // The preview must belong to the currently selected file; replacing the file
    // should always require a fresh mapping review before submission.
    const csvPreviewIsCurrent = isPreviewedSpreadsheetFormat(importFormat) && !!csvPreview && csvPreviewFileKey === selectedFileKey;
    const csvPreviewCanSubmit = csvPreviewIsCurrent && csvPreview?.canImport === true;

    useEffect(() => {
        let isMounted = true;

        agent.Atd.profile()
            .then((profile) => {
                if (isMounted) {
                    setCanUseAtdPriceFile(hasConnectedAtdPriceProfile(profile));
                }
            })
            .catch(() => {
                if (isMounted) {
                    setCanUseAtdPriceFile(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!canUseAtdPriceFile && importFormat === 'atd_price_file') {
            setImportFormat('csv_xlsx');
        }
    }, [canUseAtdPriceFile, importFormat]);

    useEffect(() => {
        if (!canUseConfigurators) {
            setConfigurators([]);
            setConfiguratorCollections([]);
            return;
        }

        let isMounted = true;
        Promise.all([agent.ProductGuides.list(), agent.ProductGuides.collections()])
            .then(([guideResults, collectionResults]) => {
                if (!isMounted) return;
                const productConfigurators = guideResults.filter((guide) => guide.guideType === 'product-configurator');
                setConfigurators(productConfigurators);
                setConfiguratorCollections(collectionResults);
                setSelectedConfiguratorId((current) => current || (productConfigurators[0]?.id ? String(productConfigurators[0].id) : ''));
            })
            .catch(() => {
                if (!isMounted) return;
                setConfigurators([]);
                setConfiguratorCollections([]);
            });

        return () => {
            isMounted = false;
        };
    }, [canUseConfigurators]);

    const loadHistory = useCallback(async () => {
        const request = ++historyRequest.current;
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            const [result, lastSuccessful, activeExport, capacity] = await Promise.all([
                agent.Imports.history('active', {
                    kind: isLegacy ? 'spreadsheets' : pageView,
                    limit: 6,
                }),
                !isLegacy && pageView === 'imports'
                    ? agent.Imports.lastSuccessful().catch(() => null)
                    : Promise.resolve(null),
                pageView === 'exports' ? agent.Imports.activeExport() : Promise.resolve(undefined),
                pageView === 'imports' ? agent.Imports.importCapacity() : Promise.resolve(undefined),
            ]);
            if (request !== historyRequest.current) return;
            setHistory(result);
            if (pageView === 'imports') setImportCapacity(capacity);
            if (pageView === 'exports') {
                setHasActiveExport(activeExport);
                if (activeExport === false) setExportState(prev => ({ ...prev, success: undefined }));
            }
            if (!isLegacy && pageView === 'imports') setLastSuccessfulFormat(lastSuccessful?.importFormat ?? null);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to load import history.');
            if (request === historyRequest.current) setHistoryError(message);
        } finally {
            if (request === historyRequest.current) setIsLoadingHistory(false);
        }
    }, [isLegacy, pageView]);

    useEffect(() => {
        reloadHistory.current = loadHistory;
        setHistory([]);
        setHasActiveExport(undefined);
        setImportCapacity(undefined);
        loadHistory();
        return () => {
            historyRequest.current++;
            reloadHistory.current = async () => {};
        };
    }, [loadHistory]);

    useEffect(() => {
        const timer = window.setInterval(() => { void loadHistory(); }, 5000);
        return () => window.clearInterval(timer);
    }, [pageView, loadHistory]);

    useEffect(() => {
        if (!csvPreviewIsCurrent) {
            setIsCsvPreviewModalOpen(false);
        }
    }, [csvPreviewIsCurrent]);

    const loadVendorSuffixRules = useCallback(async () => {
        try {
            const rules = await agent.Settings.vendorSkuRules();
            const mappedRules = rules.reduce<Record<string, VendorSkuRuleSettings>>((accumulator, rule) => {
                accumulator[rule.shopifyVendor] = {
                    skuPrefix: rule.skuPrefix || '',
                    normalizeSku: rule.normalizeSku === true,
                };
                return accumulator;
            }, {});

            setVendorSuffixRulesByVendor(mappedRules);
        } catch {
            setVendorSuffixRulesByVendor({});
        }
    }, []);

    useEffect(() => {
        if (!acesSetup?.queueId) {
            return;
        }

        loadVendorSuffixRules();
    }, [acesSetup?.queueId, loadVendorSuffixRules]);

    useEffect(() => {
        if (!acesSetup?.selectedVendor) {
            return;
        }

        const vendorRule = vendorSuffixRulesByVendor[acesSetup.selectedVendor];

        if (vendorRule === undefined) {
            return;
        }

        setAcesSetup((previous) => {
            if (!previous || previous.selectedVendor !== acesSetup.selectedVendor || (previous.skuSuffixRule === vendorRule.skuPrefix && previous.normalizeSku === vendorRule.normalizeSku)) {
                return previous;
            }

            return {
                ...previous,
                skuSuffixRule: vendorRule.skuPrefix,
                normalizeSku: vendorRule.normalizeSku,
            };
        });
    }, [acesSetup?.selectedVendor, vendorSuffixRulesByVendor]);

    const handleDropZoneDrop = useCallback((_: File[], acceptedFiles: File[]) => {
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
        const selectedFile = acceptedFiles[0];

        if (!selectedFile) {
            setUploadState({ file: undefined, error: 'No file selected.' });
            return;
        }

        if (!isValidFileForFormat(selectedFile, importFormat)) {
            setUploadState({
                file: undefined,
                success: undefined,
                error: getInvalidFileMessage(importFormat)
            });
            return;
        }

        setUploadState({ file: selectedFile, error: undefined, success: undefined });
    }, [getInvalidFileMessage, importFormat, isValidFileForFormat]);

    const handleRemoveSelectedFile = useCallback(() => {
        setUploadState({ file: undefined, error: undefined, success: undefined });
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
    }, []);

    const handleFormatChange = useCallback((value: string) => {
        const normalized = value as ImportFormat;
        setImportFormat(normalized);
        setHasSelectedImportFormat(true);
        setShowTurn14Sources(false);
        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
        setUploadState({ file: undefined, error: undefined, success: undefined });
    }, []);

    // Settings can link directly to the standard importer without creating a separate CSV workflow.
    useEffect(() => {
        if (searchParams.get('format') !== 'csv_xlsx' || pageView !== 'imports') return;
        handleFormatChange('csv_xlsx');
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.delete('format');
        setSearchParams(updatedSearchParams, { replace: true });
    }, [handleFormatChange, pageView, searchParams, setSearchParams]);

    const handleDownloadTemplate = useCallback(async () => {
        try {
            setIsDownloadingTemplate(true);
            const isCollectionMappingImport = importFormat === 'collection_mappings_csv_xlsx';
            const isConfiguratorDataImport = importFormat === 'configurator_data_csv_xlsx';
            const productGuideId = Number(selectedConfiguratorId);
            if (isConfiguratorDataImport && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
                setUploadState((prev) => ({ ...prev, success: undefined, error: 'Select a configurator before downloading its template.' }));
                return;
            }

            const collectionId = Number(configuratorTemplateCollectionId);
            const response = isCollectionMappingImport
                ? await agent.Imports.collectionMappingTemplate()
                : isConfiguratorDataImport
                    ? await agent.Imports.configuratorDataTemplate(
                        productGuideId,
                        Number.isSafeInteger(collectionId) && collectionId > 0 ? collectionId : undefined)
                    : await agent.Imports.template();
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = isCollectionMappingImport
                ? 'collection-mapping-import-template.csv'
                : isConfiguratorDataImport
                    ? 'configurator-data-import-template.csv'
                    : 'fitment-import-template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to download import template.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsDownloadingTemplate(false);
        }
    }, [configuratorTemplateCollectionId, importFormat, selectedConfiguratorId]);

    const handleQueueExport = useCallback(async () => {
        if (queueingExport.current || hasActiveExport !== false) return;
        queueingExport.current = true;
        try {
            setIsQueueingExport(true);
            setExportState((prev) => ({ ...prev, error: undefined }));
            await agent.Imports.exportFitments();
            setHasActiveExport(true);
            // Exports run through the history queue. The completed job exposes
            // the normal Download action once the worker has written the CSV.
            setExportState({
                error: undefined,
                success: 'Export started. The file will appear in export history when ready.'
            });
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to queue fitment export.');
            setExportState({ success: undefined, error: message });
        } finally {
            queueingExport.current = false;
            setIsQueueingExport(false);
        }
    }, [hasActiveExport]);

    const handlePreviewCsvXlsx = useCallback(async () => {
        if (!uploadState.file) {
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return false;
        }

        if (!isPreviewedSpreadsheetFormat(importFormat) || !isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadState((prev) => ({ ...prev, error: getInvalidFileMessage(importFormat) }));
            return false;
        }

        try {
            setIsPreviewingCsv(true);
            setUploadState((prev) => ({ ...prev, error: undefined, success: undefined }));
            const productGuideId = Number(selectedConfiguratorId);
            if (importFormat === 'configurator_data_csv_xlsx'
                && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
                setUploadState((prev) => ({ ...prev, error: 'Select a configurator before previewing the file.' }));
                return false;
            }

            // Each spreadsheet domain owns its preview so destination, variant,
            // and attribute validation can evolve independently.
            const preview = importFormat === 'collection_mappings_csv_xlsx'
                ? await agent.Imports.previewCollectionMappings(uploadState.file)
                : importFormat === 'configurator_data_csv_xlsx'
                    ? await agent.Imports.previewConfiguratorData(uploadState.file, productGuideId)
                    : await agent.Imports.previewCsvXlsx(uploadState.file);
            setCsvPreview(preview);
            setCsvPreviewFileKey(getFileKey(uploadState.file));
            setIsCsvPreviewModalOpen(true);

            if (!preview.canImport) {
                setUploadState((prev) => ({ ...prev, error: 'Fix the CSV/XLSX column mapping before submitting this import.' }));
                return false;
            }

            return true;
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to preview CSV/XLSX import.');
            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
            return false;
        } finally {
            setIsPreviewingCsv(false);
        }
    }, [getFileKey, getInvalidFileMessage, importFormat, isValidFileForFormat, selectedConfiguratorId, uploadState.file]);

    const handleSubmit = useCallback(async () => {
        if (submittingImport.current || importSubmissionDisabled) return;
        if (importFormat !== 'product_tags' && !uploadState.file) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return;
        }

        if (uploadState.file && !isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({
                ...prev,
                error: getInvalidFileMessage(importFormat)
            }));
            return;
        }

        if (isPreviewedSpreadsheetFormat(importFormat) && !csvPreviewCanSubmit) {
            await handlePreviewCsvXlsx();
            return;
        }

        const productGuideId = Number(selectedConfiguratorId);
        if (importFormat === 'configurator_data_csv_xlsx'
            && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Select a configurator before submitting the import.' }));
            return;
        }

        let parsedPriceAdjustmentValue: number | undefined;
        if (importFormat === 'atd_price_file' && priceAdjustmentMode !== 'none') {
            parsedPriceAdjustmentValue = Number(priceAdjustmentValue);

            if (!Number.isFinite(parsedPriceAdjustmentValue) || parsedPriceAdjustmentValue < 0) {
                setUploadProgressPercent(undefined);
                setUploadState((prev) => ({
                    ...prev,
                    error: 'Enter a price adjustment value greater than or equal to 0.'
                }));
                return;
            }
        }

        try {
            submittingImport.current = true;
            setIsSubmitting(true);
            setUploadProgressPercent(importFormat === 'product_tags' ? undefined : 0);
            const result = await agent.Imports.queue(
                uploadState.file,
                importFormat,
                (progressEvent) => {
                    if (!progressEvent.total || progressEvent.total <= 0) {
                        return;
                    }

                    const percent = Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100));
                    setUploadProgressPercent(percent);
                },
                importFormat === 'atd_price_file'
                    ? {
                        priceAdjustmentMode,
                        priceAdjustmentValue: parsedPriceAdjustmentValue,
                        skipZeroMapPrices,
                    }
                    : undefined,
                importFormat === 'product_tags'
                    ? {
                        productTagFormatPreset,
                        productTagCustomFormat: productTagFormatPreset === 'custom' ? productTagCustomFormat : undefined,
                        productTagValidationMode,
                    }
                    : undefined,
                importFormat === 'csv_xlsx'
                    ? {
                        csvXlsxImportMode,
                    }
                    : undefined,
                importFormat === 'configurator_data_csv_xlsx'
                    ? {
                        productGuideId,
                        configuratorAssignmentMode,
                    }
                    : undefined
            );

            setImportCapacity(current => current ? { ...current, activeCount: current.activeCount + 1 } : current);

            if (importFormat === 'aces_xml' && result.acesSetup) {
                const details = result.acesSetup;
                setAcesSetup({
                    queueId: result.queueId,
                    details,
                    selectedVendor: details.matchedVendor || '',
                    skuSuffixRule: details.vendorSkuSuffixRule || '',
                    useSkuSuffixRule: true,
                    updateVendorRule: true,
                    normalizeSku: details.normalizeSku === true,
                });

                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: 'ACES header parsed. Review vendor and SKU mapping settings, then start the import.'
                });
            } else {
                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: importFormat === 'product_tags'
                        ? 'Product Tags preview has been scheduled. Review and apply it from import history when it is ready.'
                        : importFormat === 'collection_mappings_csv_xlsx'
                            ? 'Your collection mapping import has been scheduled for processing.'
                            : importFormat === 'configurator_data_csv_xlsx'
                                ? 'Your configurator data import has been scheduled for processing.'
                            : 'Your import has been scheduled for processing.'
                });
            }

            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to queue import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
            await reloadHistory.current();
        } finally {
            submittingImport.current = false;
            setIsSubmitting(false);
            setUploadProgressPercent(undefined);
        }
    }, [configuratorAssignmentMode, csvPreviewCanSubmit, csvXlsxImportMode, getInvalidFileMessage, handlePreviewCsvXlsx, importFormat, importSubmissionDisabled, isValidFileForFormat, priceAdjustmentMode, priceAdjustmentValue, productTagCustomFormat, productTagFormatPreset, productTagValidationMode, selectedConfiguratorId, skipZeroMapPrices, uploadState.file]);

    const handlePrimaryImportAction = useCallback(() => {
        if (isPreviewedSpreadsheetFormat(importFormat) && csvPreviewCanSubmit) {
            setIsCsvPreviewModalOpen(true);
            return;
        }

        handleSubmit();
    }, [csvPreviewCanSubmit, handleSubmit, importFormat]);

    const handleCloseCsvPreviewModal = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleCancelCsvImport = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setUploadState({ file: undefined, error: undefined, success: undefined });
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleConfirmAcesSetup = useCallback(async () => {
        if (!acesSetup) {
            return;
        }

        try {
            setIsConfirmingAcesSetup(true);
            const hasVendorSelection = !!acesSetup.selectedVendor;
            await agent.Imports.configureAces(acesSetup.queueId, {
                selectedVendor: hasVendorSelection ? acesSetup.selectedVendor : undefined,
                skuSuffixRule: hasVendorSelection ? acesSetup.skuSuffixRule : undefined,
                useSkuSuffixRule: hasVendorSelection && acesSetup.useSkuSuffixRule,
                updateVendorRule: hasVendorSelection && acesSetup.updateVendorRule,
                normalizeSku: hasVendorSelection && acesSetup.normalizeSku,
            });

            setUploadState({ file: undefined, error: undefined, success: 'ACES import configured and queued for processing.' });
            setAcesSetup(undefined);
            setUploadProgressPercent(undefined);
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to configure ACES import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsConfirmingAcesSetup(false);
        }
    }, [acesSetup]);

    const handleCloseAcesModal = useCallback(() => {
        if (isConfirmingAcesSetup) {
            return;
        }

        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
    }, [isConfirmingAcesSetup]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            await reloadHistory.current();
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive import history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download import file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import file.'));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleReviewProductTags = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setProductTagPreviewError(undefined);
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const preview = await agent.Imports.productTagsPreview(item.queueId);
            setProductTagPreview(preview);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to load Product Tags preview');
            setHistoryError(await getApiErrorMessage(error, 'Failed to load Product Tags preview.'));
        } finally {
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleApproveProductTags = useCallback(async () => {
        if (!productTagPreview) {
            return;
        }

        try {
            setProductTagPreviewError(undefined);
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: true }));
            const result = await agent.Imports.approveProductTags(productTagPreview.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Applied ${result.appliedFitmentCount.toLocaleString()} fitment mapping(s) from the Product Tags preview.`
            });
            setProductTagPreview(undefined);
            await reloadHistory.current();
        } catch (error: any) {
            setProductTagPreviewError(await getApiErrorMessage(error, 'Failed to approve Product Tags import.'));
        } finally {
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: false }));
        }
    }, [productTagPreview]);

    const handleUndoProductTags = useCallback((item: FitmentImportHistoryItem) => {
        setProductTagUndoError(undefined);
        setProductTagUndoTarget(item);
    }, []);

    const confirmUndoProductTags = async () => {
        const item = productTagUndoTarget;
        if (!item || productTagUndoBusy) return;

        try {
            setProductTagUndoError(undefined);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const result = await agent.Imports.undoProductTags(item.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Undid ${result.removedFitmentMappings.toLocaleString()} fitment mapping(s) from Product Tags import ${item.queueId}.`
            });
            setProductTagUndoTarget(undefined);
            await reloadHistory.current();
        } catch (error: any) {
            setProductTagUndoError(await getApiErrorMessage(error, 'Unable to undo this import. Try again.'));
        } finally {
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    };

    const productTagPreviewRows = useMemo(() => {
        if (!productTagPreview) {
            return [];
        }

        return productTagPreview.rows.map((row) => {
            const fitment = row.isUniversal
                ? 'Universal product'
                : [row.year, row.make, row.model, row.submodel].filter(Boolean).join(' ');
            const action = row.isApplied
                ? 'Applied'
                : row.willApply
                    ? row.willCreateFitmentRecord
                        ? 'Create and apply'
                        : 'Apply'
                    : 'Skip';

            return [
                row.productTitle || `Product ${row.productId}`,
                row.productSku || '-',
                row.sourceTag,
                fitment || '-',
                action,
            ];
        });
    }, [productTagPreview]);

    const fileUpload = !uploadState.file && (
        <DropZone.FileUpload actionTitle="Choose file" actionHint={validationHint} />
    );
    const uploadedFile = uploadState.file && (
        <BlockStack gap="200">
            <InlineStack gap="300" align="space-between" blockAlign="center" wrap={false}>
                <InlineStack gap="300" align="start" blockAlign="center" wrap={false}>
                    <Thumbnail
                        size="small"
                        alt={uploadState.file.name}
                        source={NoteIcon}
                    />
                    <div>
                        <Text as="p" variant="bodyMd" fontWeight="semibold">{uploadState.file.name}</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{formatFileSize(uploadState.file.size)}</Text>
                    </div>
                </InlineStack>
                <div onClick={(event) => event.stopPropagation()}>
                    <Button variant="plain" tone="critical" onClick={handleRemoveSelectedFile}>Remove</Button>
                </div>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">Click or drop another file here to replace it.</Text>
        </BlockStack>
    );
    const csvPreviewRows = csvPreviewIsCurrent
        ? (csvPreview?.rows ?? []).map((row) => {
            const status = importFormat === 'configurator_data_csv_xlsx'
                ? row.missingRequiredValues ? 'Missing required value' : 'Ready'
                : 'missingCollectionIdentifier' in row
                ? row.missingCollectionIdentifier
                    ? 'Missing collection'
                    : row.missingRequiredValues
                        ? 'Missing required value'
                        : row.emptyFitment
                            ? 'No vehicle values'
                            : row.invalidYearRange
                                ? 'Invalid year range'
                                : 'Ready'
                : row.missingRequiredValues
                    ? 'Missing required value'
                    : row.emptyFitment
                        ? 'No fitment values'
                        : row.invalidYearRange
                            ? 'Invalid year range — will be skipped'
                        : !row.sku
                            ? (row.productId || row.productHandle ? 'Product-level' : 'Vehicle only')
                            : 'Ready';

            return [
                String(row.rowNumber),
                ...row.cells,
                'expandedYearCount' in row && (row.expandedYearCount ?? 0) > 1
                    ? `${status} (${row.expandedYearCount} years)`
                    : status,
            ];
        })
        : [];
    const csvPreviewHeadings = csvPreviewIsCurrent
        ? ['Row', ...(csvPreview?.headings ?? []), 'Status']
        : [];
    const csvColumnMappingRows = csvPreviewIsCurrent
        ? (csvPreview?.mappedColumns ?? []).map((column) => [
            column.role === 'SKU' ? 'SKU' : column.fieldLabel,
            column.columnIndex >= 0 ? column.columnName || `Column ${column.columnIndex + 1}` : 'Not found',
            column.role.startsWith('Collection')
                ? 'At least one identifier required'
                : column.required ? 'Required' : 'Optional',
        ])
        : [];
    const isCollectionMappingImport = importFormat === 'collection_mappings_csv_xlsx';
    const isConfiguratorDataImport = importFormat === 'configurator_data_csv_xlsx';
    const configuratorOptions = configurators.map((guide) => ({
        label: `${guide.name}${guide.isActive ? '' : ' (inactive)'}`,
        value: String(guide.id),
    }));
    const configuratorCollectionOptions = [
        { label: 'Currently assigned products (or blank template)', value: '' },
        ...configuratorCollections.map((collection) => ({
            label: collection.title,
            value: String(collection.id),
        })),
    ];
    const requiresFile = importFormat !== 'product_tags';
    const showsImpactSummary = importFormat === 'csv_xlsx'
        || importFormat === 'configurator_data_csv_xlsx'
        || importFormat === 'aces_xml'
        || importFormat === 'atd_price_file';
    const importImpactSummary = importFormat === 'csv_xlsx'
        ? csvXlsxImportMode === 'replace_matched_skus'
            ? 'Replaces existing fitments only for variants whose SKU is matched by this file.'
            : 'Adds imported fitments. Existing fitment assignments stay in place.'
        : importFormat === 'aces_xml'
            ? 'Parses the ACES header first, then asks you to confirm vendor and SKU matching before import.'
            : importFormat === 'configurator_data_csv_xlsx'
                ? configuratorAssignmentMode === 'update_and_assign'
                    ? 'Writes the selected configurator’s values to Shopify variant metafields, validates each complete product, and applies the configurator only where resolution is deterministic.'
                    : 'Writes the selected configurator’s values to Shopify variant metafields without changing product assignments.'
                : 'Updates matched Shopify variant prices using MAP values from the selected ATD file.';
    const primaryImportActionLabel = importFormat === 'product_tags'
        ? 'Prepare preview'
        : isPreviewedSpreadsheetFormat(importFormat)
            ? 'Review import'
            : importFormat === 'aces_xml'
                ? 'Upload and continue'
                : 'Start import';
    const importHistory = useMemo(
        () => history.filter((item) =>
            item.importFormat !== 'fitment_export'
            && (!isLegacy || item.importFormat === 'csv_xlsx')),
        [history, isLegacy]
    );
    const exportHistory = useMemo(
        () => history.filter((item) => item.importFormat === 'fitment_export'),
        [history]
    );
    const visibleHistory = pageView === 'exports' ? exportHistory : importHistory;
    const historyTitle = pageView === 'exports' ? 'Export history' : 'Import history';
    const loadHistoryErrorTitle = pageView === 'exports' ? 'Unable to load export history' : 'Unable to load import history';
    const emptyHistoryText = pageView === 'exports' ? 'No export history yet.' : 'No import history yet.';

    return (
        <Page
            title={isLegacy ? 'Bulk Import' : pageView === 'exports' ? 'Exports' : 'Imports'}
            secondaryActions={isLegacy ? undefined : <ImportsExportsToggle value={pageView} onChange={handlePageViewChange} />}
        >
            <Layout>
                <Layout.Section>
                    {pageView === 'imports' ? (
                    <Card>
                        <BlockStack gap="600">
                            {importQueueFull && <Banner tone="info" title="Import queue full"><p>{importCapacityMessage}</p></Banner>}
                            {!isLegacy ? <BlockStack gap="400">
                                <BlockStack gap="100">
                                <InlineStack align="space-between" blockAlign="center" gap="300">
                                    <Text as="h2" variant="headingMd">{showTurn14Sources ? 'Turn14 fitment data' : hasSelectedImportFormat ? selectedImportFormatOption.label : 'New import'}</Text>
                                    {(hasSelectedImportFormat || showTurn14Sources) && <Button variant="plain" disabled={isSubmitting || isPreviewingCsv || isConfirmingAcesSetup}
                                        onClick={() => { setHasSelectedImportFormat(false); setShowTurn14Sources(false); }}>Change type</Button>}
                                </InlineStack>
                                {hasSelectedImportFormat && <Text as="p" tone="subdued">{selectedImportFormatOption.description}</Text>}
                                {showTurn14Sources && <Text as="p" tone="subdued">Choose how to bring your fitment data into PartFits.</Text>}
                                </BlockStack>

                                {!hasSelectedImportFormat && !showTurn14Sources && <div role="group" aria-label="Import type">
                                    <div className="pf-import-primary-row">
                                        <ImportTypeCard
                                            label={primaryImportFormatOption.label}
                                            description={primaryImportFormatOption.description}
                                            format={primaryImportFormatOption.value}
                                            lastUsed={lastSuccessfulFormat === primaryImportFormatOption.value}
                                            onChange={() => handleFormatChange(primaryImportFormatOption.value)}
                                        />
                                        <Button
                                            icon={showMoreImportOptions ? ChevronUpIcon : ChevronDownIcon}
                                            ariaExpanded={showMoreImportOptions}
                                            ariaControls="pf-more-import-options"
                                            onClick={() => setShowMoreImportOptions((expanded) => !expanded)}
                                        >{showMoreImportOptions ? 'Fewer import options' : 'More import options'}</Button>
                                    </div>
                                    <div id="pf-more-import-options" className="pf-import-type-grid pf-import-more-options" hidden={!showMoreImportOptions}>
                                        {importFormatOptions.filter((option) => option.value !== primaryImportFormatOption.value).map((option) => (
                                            <ImportTypeCard
                                                key={option.value}
                                                label={option.label}
                                                description={option.description}
                                                format={option.value}
                                                onChange={() => handleFormatChange(option.value)}
                                            />
                                        ))}
                                        <ImportTypeCard
                                            label="Turn14 fitment data"
                                            description="Import a CSV from Data Here-to-There or connect to Turn14."
                                            format="turn14"
                                            onChange={() => setShowTurn14Sources(true)}
                                        />
                                    </div>
                                </div>}

                                {showTurn14Sources && <BlockStack gap="400">
                                    <div className="pf-turn14-import-sources">
                                        <section className="pf-turn14-import-source" aria-labelledby="pf-turn14-csv-heading">
                                            <Icon source={FileIcon} tone="subdued" />
                                            <BlockStack gap="300">
                                                <BlockStack gap="100">
                                                <Text as="h3" id="pf-turn14-csv-heading" variant="headingSm">CSV from Data Here-to-There</Text>
                                                <Text as="p" tone="subdued">Upload the fitment CSV exported from the Turn 14 Distribution app by Data Here-to-There.</Text>
                                                </BlockStack>
                                                <InlineStack gap="300" blockAlign="center">
                                                    <Button onClick={() => handleFormatChange('csv_xlsx')}>Import CSV</Button>
                                                    <Text as="span" variant="bodySm" tone="subdued">Uses Fitment spreadsheet</Text>
                                                </InlineStack>
                                            </BlockStack>
                                        </section>
                                        <section className="pf-turn14-import-source" aria-labelledby="pf-turn14-direct-heading">
                                            <Icon source={ConnectIcon} tone="subdued" />
                                            <BlockStack gap="300">
                                                <BlockStack gap="100">
                                                <Text as="h3" id="pf-turn14-direct-heading" variant="headingSm">Direct Turn14 connection</Text>
                                                <Text as="p" tone="subdued">Connect your Turn14 account to sync fitment data for your existing Shopify products.</Text>
                                                </BlockStack>
                                                <InlineStack gap="300" blockAlign="center">
                                                    <Button onClick={() => navigate('/storefront?section=reference-data&connect=turn14')}>Set up connection</Button>
                                                    <Text as="span" variant="bodySm" tone="subdued">Requires a Turn14 account</Text>
                                                </InlineStack>
                                            </BlockStack>
                                        </section>
                                    </div>
                                </BlockStack>}
                            </BlockStack> : null}

                            {isLegacy || hasSelectedImportFormat ? (
                            <>
                            {isLegacy && <BlockStack gap="100">
                                <Text as="h2" variant="headingMd">{selectedImportFormatOption.label}</Text>
                                <Text as="p" tone="subdued">{selectedImportFormatOption.description}</Text>
                            </BlockStack>}

                            {importFormat !== 'product_tags' ? (
                                <BlockStack gap="600">
                                    {isPreviewedSpreadsheetFormat(importFormat) ? (
                                        <BlockStack gap="300">
                                            {isCollectionMappingImport ? (
                                                <Banner tone="info" title="Collection mapping imports are additive">
                                                    <p>Each row maps a vehicle or year range to one existing Shopify collection. A vehicle already mapped elsewhere is reassigned; mappings omitted from the file remain unchanged. Product mappings are never changed.</p>
                                                </Banner>
                                            ) : isConfiguratorDataImport ? (
                                                <BlockStack gap="300">
                                                    {configuratorOptions.length > 0 ? (
                                                        <Select
                                                            label="Configurator"
                                                            options={configuratorOptions}
                                                            value={selectedConfiguratorId}
                                                            onChange={(value) => {
                                                                setSelectedConfiguratorId(value);
                                                                setCsvPreview(undefined);
                                                                setCsvPreviewFileKey(undefined);
                                                                setIsCsvPreviewModalOpen(false);
                                                            }}
                                                            helpText="The configurator defines the dynamic attribute columns in the template and import."
                                                        />
                                                    ) : (
                                                        <Banner tone="warning" title="Create a configurator first">
                                                            <p>No product configurators are available. Create one under {FEATURES.configurators}, connect its questions to variant metafields, then return here.</p>
                                                        </Banner>
                                                    )}

                                                    <Select
                                                        label="Template product scope"
                                                        options={configuratorCollectionOptions}
                                                        value={configuratorTemplateCollectionId}
                                                        onChange={setConfiguratorTemplateCollectionId}
                                                        helpText="Optional. Choose a collection to prefill the downloaded template with its products and variants. This does not limit which rows can be imported."
                                                        disabled={configuratorOptions.length === 0}
                                                    />

                                                    <Box paddingBlockStart="100">
                                                        <BlockStack gap="200">
                                                            <Text as="h3" variant="headingSm">Import behavior</Text>
                                                            <div
                                                                role="radiogroup"
                                                                aria-label="Configurator data import behavior"
                                                                style={{ borderTop: '1px solid var(--p-color-border-secondary)', borderBottom: '1px solid var(--p-color-border-secondary)', padding: '4px 0' }}
                                                            >
                                                                {configuratorAssignmentModeOptions.map((option) => (
                                                                    <RadioRow
                                                                        key={option.value}
                                                                        id={`configurator-import-mode-${option.value}`}
                                                                        name="configurator-import-mode"
                                                                        label={option.label}
                                                                        description={option.description}
                                                                        checked={configuratorAssignmentMode === option.value}
                                                                        onChange={() => setConfiguratorAssignmentMode(option.value as ConfiguratorAssignmentMode)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </BlockStack>
                                                    </Box>

                                                    <Banner tone="info" title="One row per Shopify variant">
                                                        <p>Variant ID is the safest identifier. SKU is also supported when it uniquely identifies a variant. Blank optional attribute cells are left unchanged.</p>
                                                    </Banner>
                                                </BlockStack>
                                            ) : (
                                                    <BlockStack gap="200">
                                                        <Text as="h3" variant="headingSm">Import behavior</Text>
                                                        <div
                                                            role="radiogroup"
                                                            aria-label="Fitment import behavior"
                                                            className="pf-import-behavior-options"
                                                        >
                                                            {csvXlsxImportModeOptions.map((option) => (
                                                                <RadioRow
                                                                    key={option.value}
                                                                    id={`csv-import-mode-${option.value}`}
                                                                    name="csv-import-mode"
                                                                    label={option.label}
                                                                    description={option.description}
                                                                    checked={csvXlsxImportMode === option.value}
                                                                    onChange={() => setCsvXlsxImportMode(option.value as CsvXlsxImportMode)}
                                                                />
                                                            ))}
                                                        </div>
                                                    </BlockStack>
                                            )}
                                        </BlockStack>
                                    ) : null}

                                    {importFormat === 'atd_price_file' ? (
                                        <BlockStack gap="300">
                                            <Select
                                                label="Price adjustment"
                                                options={priceAdjustmentOptions}
                                                onChange={(value) => setPriceAdjustmentMode(value as AtdPriceAdjustmentMode)}
                                                value={priceAdjustmentMode}
                                                helpText="The adjustment is applied to the file MAP before updating the Shopify variant price."
                                            />

                                            {priceAdjustmentMode !== 'none' && (
                                                <TextField
                                                    label={priceAdjustmentMode === 'percent' ? 'Increase MAP by' : 'Increase MAP by fixed amount'}
                                                    type="number"
                                                    value={priceAdjustmentValue}
                                                    onChange={setPriceAdjustmentValue}
                                                    autoComplete="off"
                                                    min={0}
                                                    suffix={priceAdjustmentMode === 'percent' ? '%' : '$'}
                                                />
                                            )}

                                            <Checkbox
                                                label="Skip price updates where MAP is blank or zero"
                                                checked={skipZeroMapPrices}
                                                onChange={setSkipZeroMapPrices}
                                                helpText="Prevents a price file row with MAP 0 from setting a Shopify variant price to $0.00."
                                            />
                                        </BlockStack>
                                    ) : null}

                                    <BlockStack gap="300">
                                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                        <Text as="h3" variant="headingSm">File</Text>
                                        {isPreviewedSpreadsheetFormat(importFormat) ? (
                                            <Button
                                                variant="plain"
                                                onClick={handleDownloadTemplate}
                                                loading={isDownloadingTemplate}
                                                disabled={isConfiguratorDataImport && configuratorOptions.length === 0}
                                            >
                                                Download CSV template
                                            </Button>
                                        ) : null}
                                    </InlineStack>

                                    <DropZone label="Import file" labelHidden allowMultiple={false} onDrop={handleDropZoneDrop}>
                                        {uploadedFile}
                                        {fileUpload}
                                    </DropZone>
                                    {importFormat === 'csv_xlsx' && <BlockStack gap="100">
                                        <Text as="p" variant="bodySm" tone="subdued">Enter one SKU or a comma-separated list in each SKU cell. The row’s fitment and notes apply to every listed SKU.</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">For year ranges, enter 2010-2015 in Year, or use Year and Year To columns. Both endpoints are included. Use four-digit years from 1886 to 2100. Invalid ranges are skipped.</Text>
                                    </BlockStack>}
                                    </BlockStack>

                                    {isPreviewedSpreadsheetFormat(importFormat) && csvPreviewIsCurrent ? (
                                        <Banner tone={csvPreview?.canImport ? 'success' : 'warning'} title={csvPreview?.canImport ? 'Import preview ready' : 'Import preview needs attention'}>
                                            <BlockStack gap="200">
                                                <p>{csvPreview?.canImport ? 'The file has been scanned. Review the mapped columns and sample rows before submitting.' : 'The file was scanned, but required columns are missing.'}</p>
                                                <InlineStack align="start" gap="200">
                                                    <Button onClick={() => setIsCsvPreviewModalOpen(true)}>
                                                        Review preview
                                                    </Button>
                                                    <Button variant="plain" tone="critical" onClick={handleCancelCsvImport} disabled={isSubmitting}>
                                                        Cancel import
                                                    </Button>
                                                </InlineStack>
                                            </BlockStack>
                                        </Banner>
                                    ) : null}
                                </BlockStack>
                            ) : (
                                <BlockStack gap="300">
                                    <Banner tone="info" title="Source: Synced Shopify products">
                                        <p>Reads tags from synced products and prepares a preview. No fitments change until you approve it.</p>
                                    </Banner>

                                    <Select
                                        label="Tag format"
                                        options={productTagFormatOptions}
                                        value={productTagFormatPreset}
                                        onChange={(value) => setProductTagFormatPreset(value as ProductTagFormatPreset)}
                                        helpText="Use the app default unless the products were tagged by another fitment app or supplier feed."
                                    />

                                    {productTagFormatPreset === 'custom' && (
                                        <TextField
                                            label="Custom tag pattern"
                                            value={productTagCustomFormat}
                                            onChange={setProductTagCustomFormat}
                                            autoComplete="off"
                                            helpText="Use tokens like {make}, {model}, {submodel}, and {year}. Example: {make}-{model}-{year}."
                                        />
                                    )}

                                    <Select
                                        label="Validation type"
                                        options={productTagValidationOptions}
                                        value={productTagValidationMode}
                                        onChange={(value) => setProductTagValidationMode(value as ProductTagValidationMode)}
                                        helpText="Create missing values during migration, or only import tags that already match existing app vehicle values."
                                    />
                                </BlockStack>
                            )}

                            {uploadState.error && (
                                <Banner tone="critical" title="Import validation failed">
                                    <p>{uploadState.error}</p>
                                </Banner>
                            )}

                            {uploadState.success && (
                                <Banner tone="success" title="Import status">
                                    <p>{uploadState.success}</p>
                                </Banner>
                            )}

                            {isSubmitting && uploadProgressPercent !== undefined && (
                                <BlockStack gap="100">
                                    <Text as="p" variant="bodySm" tone="subdued">Uploading file… {uploadProgressPercent}%</Text>
                                    <ProgressBar progress={uploadProgressPercent} size="small" />
                                </BlockStack>
                            )}

                            {showsImpactSummary ? (
                                    <div style={impactSummaryStyle}>
                                        <BlockStack gap="100">
                                            <Text as="p" variant="bodySm" fontWeight="semibold">What this import will do</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">{importImpactSummary}</Text>
                                        </BlockStack>
                                    </div>
                            ) : null}

                            {!requiresFile || uploadState.file ? (
                                <InlineStack align="end">
                                    <Button
                                        variant="primary"
                                        onClick={handlePrimaryImportAction}
                                        loading={isSubmitting || isPreviewingCsv}
                                        disabled={importSubmissionDisabled || (isPreviewedSpreadsheetFormat(importFormat) && csvPreviewIsCurrent && csvPreview?.canImport === false)
                                            || (isConfiguratorDataImport && !selectedConfiguratorId)}
                                    >
                                        {primaryImportActionLabel}
                                    </Button>
                                </InlineStack>
                            ) : null}
                            </>
                            ) : null}
                        </BlockStack>
                    </Card>
                    ) : (
                    <Card>
                        <BlockStack gap="300">
                            <BlockStack gap="100">
                                <Text as="h2" variant="headingMd">Fitment export</Text>
                                <Text as="p" tone="subdued">Export your current product fitment assignments to a CSV file.</Text>
                            </BlockStack>

                            {exportState.error && (
                                <Banner tone="critical" title="Export failed">
                                    <p>{exportState.error}</p>
                                </Banner>
                            )}

                            {exportState.success && (
                                <Banner tone="success" title="Export queued">
                                    <p>{exportState.success}</p>
                                </Banner>
                            )}

                            <InlineStack align="start">
                                <Button variant="primary" onClick={handleQueueExport} loading={isQueueingExport}
                                    disabled={hasActiveExport !== false}>Export fitments</Button>
                            </InlineStack>
                            {hasActiveExport && <Text as="p" tone="subdued">An export is queued or running. You can start another when it finishes.</Text>}
                        </BlockStack>
                    </Card>
                    )}
                </Layout.Section>

                <Layout.Section>
                    <Card padding="0">
                        <div className="pf-import-history-panel">
                            <div className="pf-import-history-panel__header">
                                <Text as="h2" variant="headingMd">{historyTitle}</Text>
                                <Button variant="tertiary" icon={RefreshIcon} onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </div>

                            {historyError && (
                                <Box padding="400">
                                <Banner tone="critical" title={historyErrorTitle || loadHistoryErrorTitle}>
                                    <p>{historyError}</p>
                                </Banner>
                                </Box>
                            )}

                            {visibleHistory.length === 0 && !isLoadingHistory ? (
                                <Box padding="400"><Text as="p" tone="subdued">{emptyHistoryText}</Text></Box>
                            ) : (
                                <>
                                    <ImportHistoryCards
                                        items={visibleHistory.slice(0, 5)}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        downloadingReviewIds={downloadingReviewIds}
                                        reviewingProductTagIds={reviewingProductTagIds}
                                        undoingProductTagIds={undoingProductTagIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                        onDownloadReview={handleDownloadReview}
                                        onReviewProductTags={handleReviewProductTags}
                                        onUndoProductTags={handleUndoProductTags}
                                    />
                                    <div className="pf-import-history-panel__footer">
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            {isLoadingHistory && visibleHistory.length === 0 ? 'Loading history…' : visibleHistory.length > 5 ? 'Showing latest 5 runs' : `${visibleHistory.length} ${visibleHistory.length === 1 ? 'run' : 'runs'}`}
                                        </Text>
                                            <Button
                                                variant="plain"
                                                icon={ClockIcon}
                                                onClick={() => navigate(pageView === 'exports' ? '/imports/history?view=exports' : '/imports/history')}
                                            >
                                                View all history
                                            </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                </Layout.Section>
            </Layout>

            <Modal
                open={!!productTagUndoTarget}
                onClose={() => { if (!productTagUndoBusy) setProductTagUndoTarget(undefined); }}
                title="Undo product-tag import?"
                primaryAction={{ content: 'Undo import', destructive: true, loading: productTagUndoBusy, onAction: confirmUndoProductTags }}
                secondaryActions={[{ content: 'Cancel', disabled: productTagUndoBusy, onAction: () => setProductTagUndoTarget(undefined) }]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        {productTagUndoError && <Banner tone="critical">{productTagUndoError}</Banner>}
                        <Text as="p">Fitment mappings created by {productTagUndoTarget?.originalFileName || 'this import'} will be removed.</Text>
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Modal
                open={csvPreviewIsCurrent && isCsvPreviewModalOpen}
                onClose={handleCloseCsvPreviewModal}
                title={csvPreview?.canImport
                    ? isCollectionMappingImport
                        ? 'Review collection mapping import'
                        : isConfiguratorDataImport ? 'Review configurator data import' : 'Review CSV/XLSX import'
                    : isCollectionMappingImport
                        ? 'Collection mapping preview needs attention'
                        : isConfiguratorDataImport ? 'Configurator data preview needs attention' : 'CSV/XLSX preview needs attention'}
                primaryAction={{
                    content: isCollectionMappingImport
                        ? 'Submit collection mappings'
                        : isConfiguratorDataImport
                            ? configuratorAssignmentMode === 'update_and_assign'
                                ? 'Update and assign products'
                                : 'Update variant attributes'
                        : csvXlsxImportMode === 'replace_matched_skus'
                        ? 'Submit and replace fitments'
                        : 'Submit file',
                    onAction: handleSubmit,
                    loading: isSubmitting,
                    disabled: importSubmissionDisabled || !csvPreviewCanSubmit || isPreviewingCsv,
                }}
                secondaryActions={[
                    {
                        content: 'Back to upload',
                        onAction: handleCloseCsvPreviewModal,
                    },
                    {
                        content: 'Cancel import',
                        onAction: handleCancelCsvImport,
                        destructive: true,
                    },
                ]}
            >
                <Modal.Section>
                    {csvPreviewIsCurrent && csvPreview ? (
                        <BlockStack gap="300">
                            {importQueueFull && <Banner tone="info" title="Import queue full"><p>{importCapacityMessage}</p></Banner>}
                            <Banner tone={csvPreview.canImport ? 'success' : 'warning'} title={csvPreview.canImport ? 'CSV/XLSX preview ready' : 'CSV/XLSX preview needs attention'}>
                                <p>{csvPreview.canImport ? 'Confirm the mapped columns and sample rows, then submit the import.' : 'The file was scanned, but required columns are missing.'}</p>
                            </Banner>

                            <InlineStack gap="400" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    File: {csvPreview.fileName || uploadState.file?.name || 'Selected file'}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Rows scanned: {csvPreview.scannedDataRowCount.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Mode: {isCollectionMappingImport
                                        ? 'Upsert mappings'
                                        : isConfiguratorDataImport
                                            ? configuratorAssignmentMode === 'update_and_assign' ? 'Update attributes and assign' : 'Update attributes only'
                                        : csvXlsxImportMode === 'replace_matched_skus' ? 'Replace matched SKUs' : 'Append'}
                                </Text>
                            </InlineStack>

                            {isCollectionMappingImport ? (
                                <Banner tone="info" title="Existing mappings may be reassigned">
                                    <p>If an imported vehicle is already mapped to another collection, this import moves it to the collection identified by the row. No mappings are removed merely because they are absent from the file.</p>
                                </Banner>
                            ) : isConfiguratorDataImport ? (
                                <Banner tone="info" title="Shopify metafields are the source of truth">
                                    <p>The import writes variant-level metafields in Shopify. Products are assigned only after their required values, vehicle fitments, and attribute combinations pass configurator validation.</p>
                                </Banner>
                            ) : csvXlsxImportMode === 'replace_matched_skus' ? (
                                <Banner tone="warning" title="Replace mode selected">
                                    <p>Existing fitments will be cleared only for variants whose SKU is matched by this file.</p>
                                </Banner>
                            ) : null}

                            {csvPreview.errors?.length ? (
                                <Banner tone="critical" title="Preview errors">
                                    <ul>
                                        {csvPreview.errors.map((error) => <li key={error}>{error}</li>)}
                                    </ul>
                                </Banner>
                            ) : null}

                            {csvPreview.warnings?.length ? (
                                <Banner tone="info" title="Preview notes">
                                    <ul>
                                        {csvPreview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                    </ul>
                                </Banner>
                            ) : null}

                            <BlockStack gap="200">
                                <Text as="h3" variant="headingSm">Column mapping</Text>
                                <CsvPreviewTable
                                    headings={['Import field', 'File column', 'Requirement']}
                                    rows={csvColumnMappingRows}
                                />
                            </BlockStack>

                            {csvPreviewRows.length > 0 ? (
                                <BlockStack gap="200">
                                    <Text as="h3" variant="headingSm">Sample rows</Text>
                                    <CsvPreviewTable
                                        headings={csvPreviewHeadings}
                                        rows={csvPreviewRows}
                                    />
                                    {csvPreview.hasMoreRows ? (
                                        <Text as="p" tone="subdued">Showing the first {csvPreview.rows.length} data rows.</Text>
                                    ) : null}
                                </BlockStack>
                            ) : null}
                        </BlockStack>
                    ) : null}
                </Modal.Section>
            </Modal>

            <Modal
                open={!!acesSetup}
                onClose={handleCloseAcesModal}
                title="Finalize ACES import"
                primaryAction={{
                    content: 'Start ACES import',
                    onAction: handleConfirmAcesSetup,
                    loading: isConfirmingAcesSetup,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: handleCloseAcesModal,
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" tone="subdued">Confirm vendor mapping before fitment rows are processed.</Text>

                        <Text as="p" variant="bodySm">{acesSetup?.details.documentTitle || 'Untitled ACES document'} {acesSetup?.details.company ? `· ${acesSetup.details.company}` : ''}</Text>

                        <Select
                            label="Vendor (optional)"
                            options={[
                                { label: 'No vendor (match store-wide)', value: noVendorOptionValue },
                                ...(acesSetup?.details.vendorOptions.map((v) => ({ label: v, value: v })) ?? []),
                            ]}
                            value={acesSetup?.selectedVendor || noVendorOptionValue}
                            onChange={(value) => setAcesSetup((previous) => {
                                if (!previous) {
                                    return previous;
                                }

                                const selectedVendor = value === noVendorOptionValue ? '' : value;

                                return {
                                    ...previous,
                                    selectedVendor,
                                    skuSuffixRule: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.skuPrefix ?? '') : '',
                                    useSkuSuffixRule: selectedVendor ? previous.useSkuSuffixRule : false,
                                    updateVendorRule: selectedVendor ? previous.updateVendorRule : false,
                                    normalizeSku: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.normalizeSku ?? false) : false,
                                };
                            })}
                            helpText="Leave vendor unselected to match SKUs across the entire store."
                        />

                        <TextField
                            label="Vendor SKU suffix rule"
                            value={acesSetup?.skuSuffixRule ?? ''}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, skuSuffixRule: value } : previous)}
                            autoComplete="off"
                            disabled={!acesSetup?.selectedVendor}
                            helpText="Adds this suffix when matching ACES part numbers to Shopify SKUs. Falls back to the original part number if no match is found."
                        />

                        <Checkbox
                            label="Use this suffix rule for this import"
                            checked={acesSetup?.useSkuSuffixRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, useSkuSuffixRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Normalize SKU (remove special characters before matching)"
                            checked={acesSetup?.normalizeSku ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, normalizeSku: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Update saved vendor rule with this value"
                            checked={acesSetup?.updateVendorRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, updateVendorRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Modal
                open={!!productTagPreview}
                onClose={() => {
                    if (!productTagPreview || approvingProductTagIds[productTagPreview.queueId]) {
                        return;
                    }

                    setProductTagPreview(undefined);
                    setProductTagPreviewError(undefined);
                }}
                title="Review Product Tags import"
                primaryAction={productTagPreview?.awaitingApproval ? {
                    content: 'Apply import',
                    onAction: handleApproveProductTags,
                    loading: productTagPreview ? !!approvingProductTagIds[productTagPreview.queueId] : false,
                    disabled: !productTagPreview || productTagPreview.willApplyRows === 0,
                } : undefined}
                secondaryActions={[
                    {
                        content: 'Close',
                        onAction: () => {
                            setProductTagPreview(undefined);
                            setProductTagPreviewError(undefined);
                        },
                    },
                ]}
            >
                <Modal.Section>
                    {productTagPreview && (
                        <BlockStack gap="300">
                            {productTagPreviewError && (
                                <Banner tone="critical" title="Product Tags import action failed">
                                    <p>{productTagPreviewError}</p>
                                </Banner>
                            )}

                            <InlineStack gap="400" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Products: {productTagPreview.productCount.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Ready rows: {productTagPreview.willApplyRows.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    New vehicle values: {productTagPreview.willCreateFitmentRecords.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Skipped: {productTagPreview.skippedRows.toLocaleString()}
                                </Text>
                            </InlineStack>

                            <Text as="p" variant="bodySm" tone="subdued">
                                These rows are staged only. The app source of truth is not changed until you apply the import.
                            </Text>

                            {productTagPreviewRows.length > 0 ? (
                                <DataTable
                                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                                    headings={['Product', 'SKU', 'Tag', 'Fitment', 'Action']}
                                    rows={productTagPreviewRows}
                                />
                            ) : (
                                <Text as="p" tone="subdued">No preview rows were created.</Text>
                            )}

                            {productTagPreview.totalRows > productTagPreview.rows.length && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Showing {productTagPreview.rows.length.toLocaleString()} of {productTagPreview.totalRows.toLocaleString()} staged row(s).
                                </Text>
                            )}
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default ImportsPage;
